/**
 * Commission Forecast API Service
 * Base: process.env.REACT_APP_MyAMP_Url / api/commission-report/*
 * All endpoints: POST, empty body, Bearer auth (handled by apiFetch)
 */
import { apiFetch } from '../utils/apiClient';

const REPORT = `${process.env.REACT_APP_MyAMP_Url}/api/commission-report`;

function post<T>(endpoint: string, year?: number): Promise<T> {
  return apiFetch<T>(`${REPORT}/${endpoint}`, {
    method: 'POST',
    skipCache: true,
    ...(year ? { body: JSON.stringify({ year }) } : {}),
  });
}

// ── Raw API response interfaces (exact field names from API) ─────────────────

interface ApiEnvelope<T> { row_count: number; data: T[]; }

interface YoyApiRow {
  Period: string;
  PolicyCount: number;
  UniqueClients: number;
  WrittenPremium: number;
  CommissionEarned: number;
  CommissionReceived: number;
  CommissionOutstanding: number;
  NewBiz_Commission: number;
  Renewal_Commission: number;
  Rewrite_Commission: number;
  FeeIncome: number;
  TotalRevenue: number;
  AvgCommissionPerPolicy: number;
  PL_Commission: number;
  Life_Commission: number;
  CL_Commission: number;
  YoY_CommissionChange_Dollars: number;
  YoY_CommissionChange_Pct: number;
}

interface Trailing12MApiRow {
  MonthYear: string;
  MonthLabel: string;
  PoliciesWritten: number;
  WrittenPremium: number;
  CommissionEarned: number;
  FeeIncome: number;
  TotalRevenue: number;
  NewBiz_Commission: number;
  Renewal_Commission: number;
  NewBiz_CommissionPct: number;
  CommissionReceived: number;
  CommissionStillOwed: number;
  PctStillOwed: number;
  TotalReceivables: number;
  ReceivablesCollected: number;
  ReceivablesOutstanding: number;
}

interface Forecast30DApiRow {
  ForecastBucket: string;
  PolicyCount: number;
  PremiumBase: number;
  CommissionForecast: number;
  ImpliedCommRate_Pct: number;
  RetentionFactor: number;
  AdjustedForecast: number;
  EarliestDate: string;
  LatestDate: string;
  Note: string;
}

interface Forecast90DApiRow {
  ForecastMonth: string;
  ForecastPeriod: string;
  ExpiringPolicies: number;
  ExpiringPremium: number;
  PipelineCommission: number;
  UnquotedPolicies: number;
  NonRenewalFlagged: number;
  StdDev12M: number;
  VolatilityPct: number;
  VolatilityLabel: string;
  Pess_RenewalCommission: number;
  Pess_NewBizCommission: number;
  PESSIMISTIC_Total: number;
  Base_RenewalCommission: number;
  Base_NewBizCommission: number;
  BASE_CASE_Total: number;
  Opt_RenewalCommission: number;
  Opt_NewBizCommission: number;
  OPTIMISTIC_Total: number;
  FeeIncome_Forecast: number;
  RevenueRiskRange: number;
  TrendFactor: number;
  WeightedAvgMonthlyCommission: number;
  SimpleAvgMonthlyCommission: number;
}

interface ARAgingApiRow {
  AgingBucket: string;
  BucketSort: number;
  PolicyCount: number;
  ReceivablesOwed: number;
  ReceivablesCollected: number;
  ReceivablesOutstanding: number;
  CommissionOutstanding: number;
  CollectionRatePct: number;
  CollectionFlag: string;
  SampleCarrier: string;
  CarrierCount: number;
}

// ── Display interfaces (consumed by CommissionForecastPage) ──────────────────

export interface DisplayMonthRow {
  MonthLabel: string;
  WrittenPremium: number;
  CommissionEarned: number;
  FeeIncome: number;
  TotalRevenue: number;
  NewBiz_Commission: number;
  Renewal_Commission: number;
  CommissionReceived: number;
  CommissionStillOwed: number;
  PoliciesWritten: number;
}

export interface DisplaySnapshot {
  MonthLabel: string;
  CommissionEarned: number;
  CommissionReceived: number;
  WrittenPremium: number;
  NewBiz_Commission: number;
  Renewal_Commission: number;
  FeeIncome: number;
  PoliciesWritten: number;
  UniqueClients: number;
  AvgCommPerPolicy: number;
}

export interface DisplayBucket {
  label: string;
  desc: string;
  policyCount: number;
  commission: number;
  certainty: number;
  adjusted: number;
  color: string;
  borderColor: string;
}

export interface DisplayScenario {
  period: string;
  volPct: number;
  volLabel: string;
  trendFactor: number;
  pessRenewal: number;
  pessNewBiz: number;
  pessTotal: number;
  baseRenewal: number;
  baseNewBiz: number;
  baseTotal: number;
  optRenewal: number;
  optNewBiz: number;
  optTotal: number;
  feeIncome: number;
}

export interface DisplayArRow {
  bucket: string;
  policies: number;
  owed: number;
  collected: number;
  outstanding: number;
  pct: number;
  flag: 'OK' | 'WATCH' | 'ESCALATE';
}

export interface DisplayLobEntry {
  name: string;
  value: number;
  color: string;
}

export interface AllCommissionData {
  trailing12m: DisplayMonthRow[];
  curr: DisplaySnapshot | null;
  smly: DisplaySnapshot | null;
  lobData: DisplayLobEntry[];
  forecast30d: DisplayBucket[];
  forecast90d: DisplayScenario[];
  arAging: DisplayArRow[];
}

// ── Mappers ──────────────────────────────────────────────────────────────────

const BUCKET_COLORS = ['#34d399', '#60a5fa', '#ef4444', '#fbbf24'];

function parseFlag(raw: string): 'OK' | 'WATCH' | 'ESCALATE' {
  const u = (raw || '').toUpperCase();
  if (u.startsWith('ESCALATE')) return 'ESCALATE';
  if (u.startsWith('WATCH'))    return 'WATCH';
  return 'OK';
}

function mapTrailing(r: Trailing12MApiRow): DisplayMonthRow {
  return {
    MonthLabel:          r.MonthLabel         ?? '',
    WrittenPremium:      r.WrittenPremium      ?? 0,
    CommissionEarned:    r.CommissionEarned    ?? 0,
    FeeIncome:           r.FeeIncome           ?? 0,
    TotalRevenue:        r.TotalRevenue        ?? 0,
    NewBiz_Commission:   r.NewBiz_Commission   ?? 0,
    Renewal_Commission:  r.Renewal_Commission  ?? 0,
    CommissionReceived:  r.CommissionReceived  ?? 0,
    CommissionStillOwed: r.CommissionStillOwed ?? 0,
    PoliciesWritten:     r.PoliciesWritten     ?? 0,
  };
}

function mapSnapshot(r: YoyApiRow): DisplaySnapshot {
  return {
    MonthLabel:        r.Period                  ?? '',
    CommissionEarned:  r.CommissionEarned        ?? 0,
    CommissionReceived:r.CommissionReceived       ?? 0,
    WrittenPremium:    r.WrittenPremium           ?? 0,
    NewBiz_Commission: r.NewBiz_Commission        ?? 0,
    Renewal_Commission:r.Renewal_Commission       ?? 0,
    FeeIncome:         r.FeeIncome                ?? 0,
    PoliciesWritten:   r.PolicyCount              ?? 0,
    UniqueClients:     r.UniqueClients            ?? 0,
    AvgCommPerPolicy:  r.AvgCommissionPerPolicy   ?? 0,
  };
}

function mapBucket(r: Forecast30DApiRow, i: number): DisplayBucket {
  const color = BUCKET_COLORS[i] ?? '#8b5cf6';
  // ForecastBucket e.g. "A - Renewals Due Within 30 Days"
  const parts = r.ForecastBucket.split(' - ');
  const label = parts.length > 1
    ? `${parts[0]} — ${parts.slice(1).join(' - ')}`
    : r.ForecastBucket;
  return {
    label,
    desc:        r.Note           ?? '',
    policyCount: r.PolicyCount    ?? 0,
    commission:  r.CommissionForecast ?? 0,
    certainty:   r.RetentionFactor    ?? 0,
    adjusted:    r.AdjustedForecast   ?? 0,
    color,
    borderColor: `${color}60`,
  };
}

function mapScenario(r: Forecast90DApiRow): DisplayScenario {
  return {
    period:      r.ForecastPeriod           ?? '',
    volPct:      r.VolatilityPct            ?? 0,
    volLabel:    r.VolatilityLabel          ?? 'MODERATE',
    trendFactor: r.TrendFactor              ?? 1,
    pessRenewal: r.Pess_RenewalCommission   ?? 0,
    pessNewBiz:  r.Pess_NewBizCommission    ?? 0,
    pessTotal:   r.PESSIMISTIC_Total        ?? 0,
    baseRenewal: r.Base_RenewalCommission   ?? 0,
    baseNewBiz:  r.Base_NewBizCommission    ?? 0,
    baseTotal:   r.BASE_CASE_Total          ?? 0,
    optRenewal:  r.Opt_RenewalCommission    ?? 0,
    optNewBiz:   r.Opt_NewBizCommission     ?? 0,
    optTotal:    r.OPTIMISTIC_Total         ?? 0,
    feeIncome:   r.FeeIncome_Forecast       ?? 0,
  };
}

function mapArRow(r: ARAgingApiRow): DisplayArRow {
  return {
    bucket:      r.AgingBucket          ?? '',
    policies:    r.PolicyCount          ?? 0,
    owed:        r.ReceivablesOwed      ?? 0,
    collected:   r.ReceivablesCollected ?? 0,
    outstanding: r.ReceivablesOutstanding ?? 0,
    pct:         r.CollectionRatePct    ?? 0,
    flag:        parseFlag(r.CollectionFlag),
  };
}

// ── Individual fetchers (each resolves independently) ────────────────────────

export async function fetchYoyData(year?: number) {
  const res = await post<ApiEnvelope<YoyApiRow>>('yoy-comparison', year);
  const rows = res?.data ?? [];
  return {
    curr:    rows.length >= 1 ? mapSnapshot(rows[0]) : null,
    smly:    rows.length >= 2 ? mapSnapshot(rows[1]) : null,
    lobData: rows.length >= 1 ? [
      { name: 'Commercial Lines', value: rows[0].CL_Commission   ?? 0, color: '#10b981' },
      { name: 'Personal Lines',   value: rows[0].PL_Commission   ?? 0, color: '#6366f1' },
      { name: 'Life & Health',    value: rows[0].Life_Commission ?? 0, color: '#f59e0b' },
    ] as DisplayLobEntry[] : [] as DisplayLobEntry[],
  };
}

export async function fetchTrailing12mData(year?: number): Promise<DisplayMonthRow[]> {
  const res = await post<ApiEnvelope<Trailing12MApiRow>>('trailing-12m-trend', year);
  return (res?.data ?? []).map(mapTrailing);
}

export async function fetchForecast30dData(year?: number): Promise<DisplayBucket[]> {
  const res = await post<ApiEnvelope<Forecast30DApiRow>>('30-day-forecast', year);
  return (res?.data ?? []).map(mapBucket);
}

export async function fetchForecast90dData(year?: number): Promise<DisplayScenario[]> {
  const res = await post<ApiEnvelope<Forecast90DApiRow>>('90-day-forecast', year);
  return (res?.data ?? []).map(mapScenario);
}

export async function fetchArAgingData(year?: number): Promise<DisplayArRow[]> {
  const res = await post<ApiEnvelope<ARAgingApiRow>>('ar-aging', year);
  return (res?.data ?? []).map(mapArRow);
}

// ── Public (legacy — kept for backward compat) ───────────────────────────────

export async function fetchAllCommissionData(year?: number): Promise<AllCommissionData> {
  const [yoyRes, trailRes, f30Res, f90Res, arRes] = await Promise.allSettled([
    post<ApiEnvelope<YoyApiRow>>('yoy-comparison', year),
    post<ApiEnvelope<Trailing12MApiRow>>('trailing-12m-trend', year),
    post<ApiEnvelope<Forecast30DApiRow>>('30-day-forecast', year),
    post<ApiEnvelope<Forecast90DApiRow>>('90-day-forecast', year),
    post<ApiEnvelope<ARAgingApiRow>>('ar-aging', year),
  ]);

  const trailing12m = trailRes.status === 'fulfilled'
    ? (trailRes.value?.data ?? []).map(mapTrailing)
    : [];

  let curr: DisplaySnapshot | null = null;
  let smly: DisplaySnapshot | null = null;
  let lobData: DisplayLobEntry[] = [];
  if (yoyRes.status === 'fulfilled') {
    const rows = yoyRes.value?.data ?? [];
    if (rows.length >= 1) {
      curr = mapSnapshot(rows[0]);
      lobData = [
        { name: 'Commercial Lines', value: rows[0].CL_Commission   ?? 0, color: '#10b981' },
        { name: 'Personal Lines',   value: rows[0].PL_Commission   ?? 0, color: '#6366f1' },
        { name: 'Life & Health',    value: rows[0].Life_Commission ?? 0, color: '#f59e0b' },
      ];
    }
    if (rows.length >= 2) smly = mapSnapshot(rows[1]);
  }

  const forecast30d = f30Res.status === 'fulfilled'
    ? (f30Res.value?.data ?? []).map(mapBucket)
    : [];

  const forecast90d = f90Res.status === 'fulfilled'
    ? (f90Res.value?.data ?? []).map(mapScenario)
    : [];

  const arAging = arRes.status === 'fulfilled'
    ? (arRes.value?.data ?? []).map(mapArRow)
    : [];

  return { trailing12m, curr, smly, lobData, forecast30d, forecast90d, arAging };
}
